import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';

import { generateSelectorsFromTestIds } from 'helpers/utilities';

import Button from 'components/Button';
import { SearchInput } from 'components/Form/DEPRECATED/FormElements';
import Flex from 'components/Flex';
import MagnifyingGlass from 'components/Icons/MagnifyingGlass';
import CardList from 'components/Card/CardList';
import Card from 'components/Card';

const NoSearchResultsFlex = styled(Flex)`
  p {
    max-width: 38rem;
    &:first-child {
      font-weight: 500;
      margin-bottom: 0.2rem;
    }
  }
`;

/**
 * [WizardSearchCard]
 * Card component to be used within wizard when displaying a component for searching and displaying results
 *
 * @param {string} data - results from search.
 * @param {bool} isFetching - loading prop if searching for more data
 * @param {string} searchText - text displayed in the search input
 * @param {func} onSearchTextChange - on change handler for search input
 * @param {number} [displayedResultCount] - number of search results to display initially (keeps view compact)
 * @param {func} renderItem - Individual results will be rendered using this render prop
 * NOTE: renderItem should try to use WizardSearchCard.Item as the wrapping component
 * @param {func} renderItemTombstone - render prop for item tombstone when isFetching = true
 * @param {string} [noSearchResultsTitle] - pass custom message to display when no search results from entered text
 * @param {string} [noSearchResultsSubitle] - additional custom message that can give user instructions if
 *  no search results
 */

const WizardSearchCard = props => {
  const {
    data,
    isFetching,
    searchText,
    searchError,
    onSearchTextChange,
    displayedResultCount = 2,
    renderItem,
    renderItemTombstone,
    noSearchResultsTitle = `No results found for "${props.searchText}"`,
    noSearchResultsSubtitle = 'Please try again with a different search query'
  } = props;

  const [allResultsDisplayed, setAllResultsDisplayed] = useState(false);

  // If the data changes, reset 'allResultsDisplayed' flag
  useEffect(() => {
    setAllResultsDisplayed(false);
  }, [data]);

  return (
    <div>
      <SearchInput
        value={searchText}
        onChange={onSearchTextChange}
        data-testid={WizardSearchCard.TEST_IDS.SEARCH_INPUT}
        error={searchError}
      />

      {(data.length > 0 || isFetching) && (
        <CardList
          data-testid={WizardSearchCard.TEST_IDS.CONTENT}
          scrollable
          maxHeight="28.5rem"
        >
          {isFetching
            ? Array(displayedResultCount)
                .fill()
                .map((__, index) =>
                  // eslint-disable-next-line react/jsx-indent, react/no-array-index-key
                  renderItemTombstone(index)
                )
            : data.map((itemData, index) => {
                if (index >= displayedResultCount && !allResultsDisplayed) {
                  return null;
                }

                return renderItem({ data: itemData, index });
              })}
        </CardList>
      )}

      {!isFetching && data.length === 0 && searchText && (
        <Card shadow={false} data-testid={WizardSearchCard.TEST_IDS.NO_RESULTS}>
          <Card.Content>
            <NoSearchResultsFlex
              justify="center"
              align="center"
              className="mt-3 mb-4 text-center"
              column
            >
              <MagnifyingGlass size={32} className="mb-1" />
              <div>
                <p>{noSearchResultsTitle}</p>
                <p>{noSearchResultsSubtitle}</p>
              </div>
            </NoSearchResultsFlex>
          </Card.Content>
        </Card>
      )}

      {!allResultsDisplayed && data.length > displayedResultCount && !isFetching && (
        <Button
          onClick={() => setAllResultsDisplayed(true)}
          color={Button.COLORS.LINK}
          className="mt-2"
        >
          Show More Results
        </Button>
      )}
    </div>
  );
};

WizardSearchCard.propTypes = {
  data: PropTypes.arrayOf(PropTypes.shape()),
  isFetching: PropTypes.bool,
  searchText: PropTypes.string,
  searchError: PropTypes.string,
  displayedResultCount: PropTypes.number,
  onSearchTextChange: PropTypes.func,
  noSearchResultsTitle: PropTypes.string,
  noSearchResultsSubtitle: PropTypes.string,
  renderItem: PropTypes.func,
  renderItemTombstone: PropTypes.func
};

WizardSearchCard.Item = CardList.Item;

WizardSearchCard.TEST_IDS = {
  SEARCH_INPUT: 'WizardSearchCard-search-input',
  CONTENT: 'WizardSearchCard-content',
  NO_RESULTS: 'WizardSearchCard-no-results'
};

WizardSearchCard.DEFAULT_SUBTITLE = 'Please try again with a different search query';

WizardSearchCard.SELECTORS = generateSelectorsFromTestIds(WizardSearchCard.TEST_IDS);

export default WizardSearchCard;
